import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useAdminContext } from '@/hooks/useAdminContext';
import { MainLayout } from '@/components/layout/MainLayout';
import { LibraryCard } from '@/components/library/LibraryCard';
import { PromptDetailModal } from '@/components/shared/PromptDetailModal';
import { RemixersModal } from '@/components/feed/RemixersModal';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import type { LibraryPrompt } from '@/hooks/useLibraryData';

type SortOption = 'date-desc' | 'date-asc' | 'points-desc' | 'points-asc';

interface OrgPromptRow {
  score_id: string;
  submission_id: string | null;
  score_total: number;
  detected_type: string | null;
  best_prompt: string | null;
  user_id: string;
  user_name: string | null;
  created_at: string;
  evaluation_markdown: string | null;
  favorite_count: number;
  remix_count: number;
  recent_remixers: Array<{ user_id: string; user_initials: string }> | null;
  tags: string[] | null;
}

interface PromptDetailData {
  score_id: string;
  overall_score: number;
  original_score?: number | null;
  detected_type: string | null;
  best_prompt_extracted: string | null;
  dimension_scores_json: Record<string, unknown> | null;
  evaluation_markdown: string | null;
  chatcraft_subscores_json: Record<string, number> | null;
  original_content: string | null;
  submission_type: string | null;
  points_total: number | null;
  points_base?: number | null;
  points_rubric?: number | null;
  points_bonus?: number | null;
  user_name?: string | null;
  created_at: string;
  tags?: string[];
  submission_id?: string | null;
  is_public?: boolean | null;
  user_id?: string | null;
}

type OrgLibraryPrompt = LibraryPrompt & {
  owner_name?: string | null;
};

function toLibraryPrompt(row: OrgPromptRow): OrgLibraryPrompt {
  return {
    score_id: row.score_id,
    overall_score: row.score_total,
    original_score: null,
    detected_type: row.detected_type || 'Unknown',
    best_prompt: row.best_prompt,
    original_content: null,
    submission_type: null,
    dimension_scores_json: null,
    evaluation_markdown: row.evaluation_markdown,
    chatcraft_subscores_json: null,
    points_total: null,
    points_base: null,
    points_rubric: null,
    points_bonus: null,
    created_at: row.created_at,
    tags: row.tags || [],
    favorite_count: row.favorite_count || 0,
    remix_count: row.remix_count || 0,
    recent_remixers: row.recent_remixers || [],
    is_public: true,
    submission_id: row.submission_id || undefined,
    owner_name: row.user_name,
  };
}

export default function ExecPromptLibrary() {
  const { membership } = useAuth();
  const { isImpersonating, impersonatedOrgId, impersonatedOrgName } = useAdminContext();
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState<OrgLibraryPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [sortOption, setSortOption] = useState<SortOption>('date-desc');
  const [selectedPrompt, setSelectedPrompt] = useState<OrgLibraryPrompt | null>(null);
  const [promptDetail, setPromptDetail] = useState<PromptDetailData | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showRemixersModal, setShowRemixersModal] = useState(false);
  const [remixersPromptId, setRemixersPromptId] = useState<string | null>(null);
  const [remixersCount, setRemixersCount] = useState(0);
  const effectiveOrgId = isImpersonating ? impersonatedOrgId : membership?.org_id;
  const effectiveOrgName = isImpersonating ? impersonatedOrgName : membership?.org_name;

  const fetchPrompts = useCallback(async () => {
    if (!effectiveOrgId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase.rpc('get_org_prompt_feed', {
      _org_id: effectiveOrgId,
      _limit: 500,
      _before_timestamp: null,
    });

    if (error) {
      console.error('Error fetching org prompt library:', error);
      setPrompts([]);
    } else {
      setPrompts(((data || []) as OrgPromptRow[]).map(toLibraryPrompt));
    }
    setLoading(false);
  }, [effectiveOrgId]);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    prompts.forEach((prompt) => {
      const category = prompt.detected_type || 'Unknown';
      counts[category] = (counts[category] || 0) + 1;
    });
    return counts;
  }, [prompts]);

  const filteredPrompts = useMemo(() => {
    const filtered = categoryFilter === 'All'
      ? prompts
      : prompts.filter((prompt) => (prompt.detected_type || 'Unknown') === categoryFilter);

    return [...filtered].sort((a, b) => {
      if (sortOption === 'points-desc' || sortOption === 'points-asc') {
        const aScore = a.overall_score || 0;
        const bScore = b.overall_score || 0;
        return sortOption === 'points-desc' ? bScore - aScore : aScore - bScore;
      }
      const aTime = new Date(a.created_at).getTime();
      const bTime = new Date(b.created_at).getTime();
      return sortOption === 'date-desc' ? bTime - aTime : aTime - bTime;
    });
  }, [categoryFilter, prompts, sortOption]);

  const categoryNames = useMemo(() => Object.keys(categoryCounts).sort(), [categoryCounts]);
  const sortLabel = {
    'date-desc': 'Newest first',
    'date-asc': 'Oldest first',
    'points-desc': 'Score high to low',
    'points-asc': 'Score low to high',
  }[sortOption];

  const handleViewDetail = async (prompt: OrgLibraryPrompt) => {
    if (!effectiveOrgId) return;
    setSelectedPrompt(prompt);
    setPromptDetail(null);
    setDetailLoading(true);

    const { data, error } = await supabase.rpc('get_prompt_detail_for_org', {
      _score_id: prompt.score_id,
      _org_id: effectiveOrgId,
    });

    if (error) {
      console.error('Error fetching prompt detail:', error);
      toast.error('Failed to load prompt details');
      setSelectedPrompt(null);
      setDetailLoading(false);
      return;
    } else if (data && data.length > 0) {
      const detail = data[0];
      setPromptDetail({
        score_id: detail.score_id,
        overall_score: detail.overall_score,
        original_score: detail.original_score,
        detected_type: detail.detected_type,
        best_prompt_extracted: detail.best_prompt_extracted,
        dimension_scores_json: detail.dimension_scores_json as Record<string, unknown>,
        evaluation_markdown: detail.evaluation_markdown,
        chatcraft_subscores_json: detail.chatcraft_subscores_json as Record<string, number>,
        original_content: detail.original_content,
        submission_type: detail.submission_type,
        points_total: detail.points_total,
        points_base: detail.points_base,
        points_rubric: detail.points_rubric,
        points_bonus: detail.points_bonus,
        user_name: detail.user_name,
        created_at: detail.created_at,
        tags: prompt.tags,
        submission_id: detail.submission_id,
        is_public: detail.is_public,
        user_id: detail.user_id,
      });
    }
    setDetailLoading(false);
  };

  return (
    <MainLayout>
      <div className="mx-auto max-w-4xl space-y-6 p-4 md:p-6">
        <div>
          <h1 className="text-2xl font-display font-bold md:text-3xl">Organization Prompt Library</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Org-wide prompts created across {effectiveOrgName || 'the team'}.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <SlidersHorizontal className="h-4 w-4" />
                  Filter
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuLabel>Sort by</DropdownMenuLabel>
                <DropdownMenuRadioGroup value={sortOption} onValueChange={(value) => setSortOption(value as SortOption)}>
                  <DropdownMenuRadioItem value="date-desc">Date: newest first</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="date-asc">Date: oldest first</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="points-desc">Score: high to low</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="points-asc">Score: low to high</DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5 text-xs text-muted-foreground">Current: {sortLabel}</div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <button
              onClick={() => setCategoryFilter('All')}
              className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                categoryFilter === 'All'
                  ? 'border-amber-500/50 bg-amber-500/20 text-amber-400'
                  : 'border-border/50 bg-muted/30 text-muted-foreground hover:bg-muted/50'
              }`}
            >
              All ({prompts.length})
            </button>
            {categoryNames.map((category) => (
              <button
                key={category}
                onClick={() => setCategoryFilter(category)}
                className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                  categoryFilter === category
                    ? 'border-amber-500/50 bg-amber-500/20 text-amber-400'
                    : 'border-border/50 bg-muted/30 text-muted-foreground hover:bg-muted/50'
                }`}
              >
                {category} ({categoryCounts[category]})
              </button>
            ))}
          </div>
        </div>

        <div className="min-h-[400px]">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((item) => (
                <Skeleton key={item} className="h-40 w-full" />
              ))}
            </div>
          ) : filteredPrompts.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No org prompts found.</p>
          ) : (
            <div className="divide-y divide-border/30">
              {filteredPrompts.map((prompt) => (
                <LibraryCard
                  key={prompt.score_id}
                  prompt={prompt}
                  onViewDetail={handleViewDetail}
                  onRemix={(p) => navigate('/build/new', {
                    state: {
                      remixContext: { originalPrompt: p.best_prompt || p.original_content, originalScoreId: p.score_id },
                      returnTo: '/exec-dashboard/library',
                    },
                  })}
                  onViewRemixers={(p) => {
                    setRemixersPromptId(p.score_id);
                    setRemixersCount(p.remix_count || 0);
                    setShowRemixersModal(true);
                  }}
                  ownerName={prompt.owner_name || undefined}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <PromptDetailModal
        isOpen={!!selectedPrompt}
        onClose={() => {
          setSelectedPrompt(null);
          setPromptDetail(null);
        }}
        data={promptDetail}
        loading={detailLoading}
        showUserName
      />

      {remixersPromptId && (
        <RemixersModal
          isOpen={showRemixersModal}
          onClose={() => {
            setShowRemixersModal(false);
            setRemixersPromptId(null);
          }}
          scoreId={remixersPromptId}
          totalCount={remixersCount}
        />
      )}
    </MainLayout>
  );
}
